// ============================================================
// PresenceAvatar.tsx
// The collaborative-PRESENCE avatar chip — deliberately the SAME look as the
// Yjs rich-text cursor's flag (renderAvatarCaret + .collab-caret__avatar in
// RichTextEditor.tsx / index.css): a ~20px round chip filled with the OWNER's
// colour, showing their photo (avatarPhotoUrl) if present, else their white
// initials. Using this everywhere presence is shown (the FormField field-owner
// indicator + the in-text caret flag share this exact visual language) means a
// person looks identical whether they're editing a rich-text note or a form
// field.
//
// ★ Given the person's userId (every caller passes it), it renders the shared
// UserAvatar — the same face as everywhere else in the app, incl. their
// avatarStyle. Only WITHOUT an id does it fall back to the old always-solid
// owner-colour chip below (the rich-text editor's own caret flag still draws
// that solid look itself).
// ============================================================
import React from 'react'
import { DEFAULT_AVATAR_COLOUR } from './Avatar'
import UserAvatar from './UserAvatar'

// Two-initial derivation from a display name (first word + last word), matching
// the CollabUser.initials the caret flag uses (useCollabUser: firstName[0]+lastName[0]).
const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0][0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''
  return `${first}${last}`.toUpperCase() || '?'
}

interface PresenceAvatarProps {
  /** The person's user id → rendered via the shared UserAvatar (preferred). */
  userId?: string
  /** Display name — drives the initials fallback + the aria/title. */
  name: string
  /** Owner's avatarColour (hex). Falls back to the brand default when empty. */
  colour?: string
  /** Presigned avatar photo URL — takes precedence over initials, exactly like
   *  the caret flag. */
  photoUrl?: string | null
  /** Hover tooltip (defaults to the name). */
  title?: string
}

const PresenceAvatar: React.FC<PresenceAvatarProps> = ({ userId, name, colour, photoUrl, title }) => {
  // ★ With the person's id: the shared UserAvatar (their account's current
  // photo / colour / style — the same face as everywhere else); the presence
  // payload's colour / photo only if they're not in the firm list.
  if (userId) {
    return (
      <span title={title ?? name} aria-label={name} className="inline-flex shrink-0">
        <UserAvatar userId={userId} fallbackName={name} fallbackLook={{ colour, photoUrl }} size="xs" title={false} />
      </span>
    )
  }
  const bg = colour && colour.length > 0 ? colour : DEFAULT_AVATAR_COLOUR
  return (
    <span
      title={title ?? name}
      aria-label={name}
      // w-5 h-5 (~20px) round chip, solid owner colour, white 600 initials —
      // mirrors .collab-caret__avatar. overflow-hidden clips the photo to the
      // circle; leading-none + text-[10px] keeps 2 initials centred.
      className="inline-flex items-center justify-center w-5 h-5 rounded-full overflow-hidden text-white text-[10px] font-semibold leading-none select-none shrink-0"
      style={{ backgroundColor: bg }}
    >
      {photoUrl
        ? <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
        : initialsOf(name)}
    </span>
  )
}

export default PresenceAvatar
